import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { faqs, siteMeta } from "@/mocks/meta";

export default function Faq() {
  useSeo("常见问题｜吃瓜精选——关于专题合集与分类盘点的疑问", "关于吃瓜精选的常见问题：专题合集与分类的区别、内容更新节奏、为何不做快讯、以及投稿与提供线索的方式，这里都有解答。");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* hero */}
      <section className="relative overflow-hidden border-b border-background-200/70 bg-background-100/50">
        <div className="pointer-events-none absolute -right-6 -top-8 select-none text-[12rem] font-black leading-none text-accent-500/[0.08]">?</div>
        <div className="wrap py-12 md:py-16">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-secondary-600" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">常见问题</span>
          </nav>
          <p className="eyebrow mt-8"><span className="eyebrow-line" />FAQ</p>
          <h1 className="serif mt-3 max-w-2xl text-3xl font-black leading-tight text-foreground-950 md:text-5xl">常见问题</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">关于吃瓜精选的整理方式与内容定位，如果你有疑问，也许下面已经有人问过。</p>
        </div>
      </section>

      {/* faq list */}
      <section className="wrap py-12 md:py-16" aria-label="问题列表">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <h2 className="serif text-xl font-bold text-foreground-950">提问快速跳转</h2>
              <ul className="mt-4 space-y-2">
                {faqs.map((f, i) => (
                  <li key={f.q}>
                    <a href={`#faq-${i + 1}`} className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700">
                      <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-accent-500 text-[10px] font-bold text-foreground-950">{i + 1}</span>
                      <span className="line-clamp-1">{f.q}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <details key={f.q} id={`faq-${i + 1}`} className="group scroll-mt-24 rounded-xl border border-background-200/70 bg-background-100/40" open={i === 0}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 text-[15px] font-bold text-foreground-950 md:px-6">
                    {f.q}
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-background-200/90 text-foreground-700 transition-transform group-open:rotate-45">
                      <i className="ri-add-line" />
                    </span>
                  </summary>
                  <div className="px-5 pb-5 md:px-6">
                    <p className="text-[15px] leading-relaxed text-foreground-700">{f.a}</p>
                  </div>
                </details>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-primary-200 bg-primary-100/50 p-6 sm:flex-row sm:items-center">
              <div>
                <h2 className="serif text-lg font-bold text-foreground-950">没找到你想要的答案？</h2>
                <p className="mt-1 text-sm text-foreground-600">关于投稿、提供线索或其他疑问，欢迎直接来信与我们聊聊。</p>
              </div>
              <a href={`mailto:${siteMeta.contactEmail}`} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-mail-send-line" /> 联系我们
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}